import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import "./style.css";
import { GameAudio } from "./game/audio";
import { PlayerController } from "./game/player";
import { GameUi } from "./game/ui";
import { SkyWorld, type CoreInteraction } from "./game/world";
import type { QualityLevel, SaveData, WorldApiResponse } from "./shared/types";

const STORAGE_KEY = "windcore-isles-save-v1";
const canvasElement = document.getElementById("game-canvas");
if (!(canvasElement instanceof HTMLCanvasElement)) throw new Error("找不到 3D 遊戲畫布");
const canvas: HTMLCanvasElement = canvasElement;

let renderer: THREE.WebGLRenderer | null = null;
let composer: EffectComposer | null = null;
let bloomPass: UnrealBloomPass | null = null;
let gameScene: THREE.Scene | null = null;
let gameCamera: THREE.PerspectiveCamera | null = null;
let world: SkyWorld | null = null;
let player: PlayerController | null = null;
let saveData: SaveData | null = null;
let currentInteraction: CoreInteraction | null = null;
let currentQuality: QualityLevel = detectQuality();
let started = false;
let paused = true;
let currentRegionId = "";
let finalMessageShown = false;
let autoAdjustedQuality = false;

const audio = new GameAudio();
const ui = new GameUi({
  onStart: () => startGame(),
  onPause: () => pauseGame(),
  onResume: () => resumeGame(),
  onQualityChange: (quality) => applyQuality(quality, true),
  onSoundToggle: () => toggleSound(),
  onFullscreen: () => toggleFullscreen(),
  onReset: () => resetProgress(),
  onTouchMove: (x, y) => player?.setTouchMovement(x, y),
  onTouchLook: (deltaX, deltaY) => player?.addTouchLook(deltaX, deltaY),
  onTouchAction: (action, pressed) => player?.triggerTouchAction(action, pressed),
});

void initialize().catch((error: unknown) => {
  console.error(error);
  ui.setLoading(1, "世界載入失敗，請重新載入後再試一次。");
  ui.showContextLost();
});

async function initialize(): Promise<void> {
  ui.setLoading(0.05, "正在檢查天空航行裝置…");
  if (!supportsWebGL()) {
    ui.showUnsupported();
    return;
  }

  await nextFrame();
  ui.setLoading(0.16, "正在連接風核觀測站…");
  const worldConfig = await fetchWorldConfig();

  await nextFrame();
  ui.setLoading(0.28, "正在描繪雲層與遠方群島…");
  saveData = loadSave(worldConfig);
  currentQuality = saveData.quality;
  audio.setEnabled(saveData.soundEnabled);
  ui.setQuality(currentQuality);
  ui.setSoundEnabled(saveData.soundEnabled);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(58, window.innerWidth / window.innerHeight, 0.08, 720);
  gameScene = scene;
  gameCamera = camera;
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: currentQuality !== "low",
    powerPreference: "high-performance",
    alpha: false,
    stencil: false,
    depth: true,
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  renderer.shadowMap.enabled = currentQuality !== "low";
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setSize(window.innerWidth, window.innerHeight, false);

  const renderPass = new RenderPass(scene, camera);
  bloomPass = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    currentQuality === "high" ? 0.28 : 0.18,
    0.34,
    0.82,
  );
  composer = new EffectComposer(renderer);
  composer.addPass(renderPass);
  composer.addPass(bloomPass);
  composer.addPass(new OutputPass());

  await nextFrame();
  ui.setLoading(0.42, "正在讓草木迎著風生長…");
  world = new SkyWorld(
    scene,
    worldConfig.dailySeed,
    currentQuality,
    worldConfig.weather.cloudDensity,
  );

  await nextFrame();
  ui.setLoading(0.76, "正在組裝測繪員的滑翔翼…");
  player = new PlayerController(
    scene,
    camera,
    canvas,
    {
      groundHeightAt: (x, z) => world?.groundHeightAt(x, z) ?? null,
      resolveMovement: (previous, desired, feetY) =>
        world?.resolvePlayerMovement(previous, desired, feetY) ?? desired,
      cameraColliders: world.cameraColliders,
    },
    {
      onInteract: () => interactWithCore(),
      onRespawn: () => {
        audio.playRespawn();
        ui.toast("風把你送回安全地點", "墜落不會失去任何探索進度。");
      },
      onGlideChange: (gliding) => {
        audio.setGliding(gliding);
        ui.setGliding(gliding);
      },
      onPointerUnlock: () => pauseGame(),
    },
  );

  world.applyProgress(saveData.activatedCores, saveData.collectedShards);
  player.setCheckpoint(new THREE.Vector3(
    saveData.checkpoint.x,
    saveData.checkpoint.y,
    saveData.checkpoint.z,
  ));
  player.teleport(new THREE.Vector3(
    saveData.checkpoint.x,
    saveData.checkpoint.y,
    saveData.checkpoint.z,
  ));
  updateProgressUi();
  applyQuality(currentQuality, false);

  await nextFrame();
  ui.setLoading(0.94, "正在開啟最後一道雲門…");
  bindWindowEvents();
  startRenderLoop();
  canvas.dataset.gameState = "ready";
  ui.markReady(worldConfig.message);
}

function startGame(): void {
  if (!player || !world || !renderer || started) return;
  started = true;
  paused = false;
  canvas.dataset.gameState = "playing";
  audio.setEnabled(saveData?.soundEnabled ?? true);
  audio.setPaused(false);
  void audio.start();
  ui.enterGame();
  player.setEnabled(true);
  player.requestPointerLock();
  const region = world.getRegionAt(player.position);
  currentRegionId = region.id;
  ui.showLocation(region.label);
  ui.toast("測繪任務開始", "喚醒三座風核塔；在空中按住 F 可展開滑翔翼。");
}

function pauseGame(): void {
  if (!started || paused) return;
  paused = true;
  canvas.dataset.gameState = "paused";
  player?.setPaused(true);
  audio.setPaused(true);
  updateAudioDebugState();
  ui.setPaused(true);
}

function resumeGame(): void {
  if (!started || !paused) return;
  paused = false;
  canvas.dataset.gameState = "playing";
  ui.setPaused(false);
  player?.setPaused(false);
  audio.setPaused(false);
  updateAudioDebugState();
  player?.requestPointerLock();
}

function interactWithCore(): void {
  if (!world || !player || paused || !currentInteraction || !saveData) return;
  const result = world.activateCore(currentInteraction.id);
  if (!result) return;
  saveData.activatedCores = [...new Set([...saveData.activatedCores, currentInteraction.id])];
  saveData.checkpoint = {
    x: result.checkpoint.x,
    y: result.checkpoint.y,
    z: result.checkpoint.z,
  };
  player.setCheckpoint(result.checkpoint);
  audio.playCore();
  ui.toast(`${result.label}已經甦醒`, "一條新的風脈正在群島之間流動。");
  currentInteraction = null;
  ui.showInteraction(null);
  if (world.activeCoreCount === world.totalCoreCount) {
    world.unlockSanctum();
    ui.toast("通往星盤聖所的風路已開啟", "回到主島北側，踏上發光的浮石即可前往最高空域。");
  }
  updateProgressUi();
  persistSave();
}

function collectShards(): void {
  if (!world || !player || !saveData) return;
  const collected = world.collectNearbyShards(player.position);
  if (collected.length === 0) return;
  for (const shard of collected) {
    saveData.collectedShards = [...new Set([...saveData.collectedShards, shard.id])];
    audio.playShard();
    ui.toast("找到記憶晶片", shard.label);
  }
  updateProgressUi();
  persistSave();
}

function updateProgressUi(): void {
  if (!world) return;
  ui.setProgress(
    world.collectedShardCount,
    world.totalShardCount,
    world.activeCoreCount,
    world.totalCoreCount,
  );
}

function applyQuality(quality: QualityLevel, persist: boolean): void {
  currentQuality = quality;
  const maximumPixelRatio = quality === "high" ? 2 : quality === "medium" ? 1.45 : 1;
  renderer?.setPixelRatio(Math.min(window.devicePixelRatio, maximumPixelRatio));
  if (renderer) renderer.shadowMap.enabled = quality !== "low";
  if (bloomPass) bloomPass.strength = quality === "high" ? 0.28 : quality === "medium" ? 0.16 : 0;
  world?.setQuality(quality);
  ui.setQuality(quality);
  resizeRenderer();
  if (saveData) {
    saveData.quality = quality;
    if (persist) persistSave();
  }
}

function toggleSound(): void {
  if (!saveData) return;
  saveData.soundEnabled = !saveData.soundEnabled;
  audio.setEnabled(saveData.soundEnabled);
  updateAudioDebugState();
  ui.setSoundEnabled(saveData.soundEnabled);
  persistSave();
}

function toggleFullscreen(): void {
  if (document.fullscreenElement) {
    void document.exitFullscreen();
    return;
  }
  const shell = document.getElementById("game-shell");
  if (shell) void shell.requestFullscreen().catch(() => {
    ui.toast("無法開啟全螢幕", "瀏覽器或 WordPress 可能限制了這項功能。");
  });
}

function resetProgress(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 即使瀏覽器封鎖儲存空間，仍可重新載入遊戲。
  }
  window.location.reload();
}

function persistSave(): void {
  if (!saveData) return;
  saveData.updatedAt = new Date().toISOString();
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saveData));
  } catch {
    ui.toast("無法保存進度", "瀏覽器目前封鎖了本機儲存，但你仍然可以繼續遊玩。");
  }
}

function startRenderLoop(): void {
  if (!renderer || !composer || !world || !player) return;
  const clock = new THREE.Clock();
  let fpsFrames = 0;
  let fpsElapsed = 0;
  let debugFrame = 0;

  const render = (): void => {
    requestAnimationFrame(render);
    const delta = Math.min(clock.getDelta(), 0.05);
    const elapsed = clock.elapsedTime;
    if (document.hidden) return;

    if (!paused && started) {
      player!.update(delta, elapsed);
      world!.update(delta, elapsed, player!.position);
      debugFrame += 1;
      if (debugFrame % 12 === 0) {
        canvas.dataset.playerPosition = [player!.position.x, player!.position.y, player!.position.z]
          .map((value) => value.toFixed(2))
          .join(",");
        canvas.dataset.viewPitch = player!.viewPitch.toFixed(4);
        updateAudioDebugState();
      }
      collectShards();
      currentInteraction = world!.findCoreInteraction(player!.position);
      ui.showInteraction(currentInteraction?.label ?? null);
      ui.setCompass(player!.viewYaw);

      const region = world!.getRegionAt(player!.position);
      if (region.id !== currentRegionId) {
        currentRegionId = region.id;
        ui.showLocation(region.label);
      }
      if (
        !finalMessageShown
        && region.id === "stargazer-sanctum"
        && world!.activeCoreCount === world!.totalCoreCount
      ) {
        finalMessageShown = true;
        ui.toast("星盤重新看見了天空", "你完成了首章探索。十枚記憶晶片仍散落在群島各處。");
      }

      fpsFrames += 1;
      fpsElapsed += delta;
      if (fpsElapsed >= 6 && !autoAdjustedQuality) {
        const fps = fpsFrames / fpsElapsed;
        fpsFrames = 0;
        fpsElapsed = 0;
        if (fps < 28 && currentQuality !== "low") {
          autoAdjustedQuality = true;
          const nextQuality: QualityLevel = currentQuality === "high" ? "medium" : "low";
          applyQuality(nextQuality, true);
          ui.toast("已自動調整畫質", "裝置負擔較高，畫面已切換成更流暢的設定。");
        }
      }
    } else {
      player!.update(delta, elapsed);
      world!.update(delta * 0.35, elapsed, player!.position);
    }

    if (currentQuality === "low" && gameScene && gameCamera) renderer!.render(gameScene, gameCamera);
    else composer!.render();
  };

  render();
}

function bindWindowEvents(): void {
  window.addEventListener("resize", resizeRenderer);
  window.addEventListener("orientationchange", () => window.setTimeout(resizeRenderer, 120));
  window.addEventListener("pointerdown", retryAudioFromUserGesture, {
    capture: true,
    passive: true,
  });
  if (typeof globalThis.PointerEvent === "undefined") {
    window.addEventListener("touchstart", retryAudioFromUserGesture, {
      capture: true,
      passive: true,
    });
  }
  window.addEventListener("keydown", (event) => {
    retryAudioFromUserGesture();
    if (event.code !== "Escape" || event.repeat || !started) return;
    if (paused) resumeGame();
    else if (document.pointerLockElement !== canvas) pauseGame();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden && started && !paused) pauseGame();
  });
  canvas.addEventListener("webglcontextlost", (event) => {
    event.preventDefault();
    canvas.dataset.gameState = "error";
    pauseGame();
    ui.showContextLost();
  });
  canvas.addEventListener("webglcontextrestored", () => window.location.reload());
}

function retryAudioFromUserGesture(): void {
  if (!started || paused || !saveData?.soundEnabled) return;
  void audio.start();
}

function updateAudioDebugState(): void {
  canvas.dataset.webAudioState = audio.webAudioState;
  canvas.dataset.webAudioError = audio.webAudioErrorMessage;
  const musicState = audio.backgroundMusicState;
  if (!musicState) return;
  canvas.dataset.musicSrc = musicState.src;
  canvas.dataset.musicLoop = String(musicState.loop);
  canvas.dataset.musicPaused = String(musicState.paused);
  canvas.dataset.musicEnded = String(musicState.ended);
  canvas.dataset.musicVolume = musicState.volume.toFixed(2);
  canvas.dataset.musicTime = musicState.currentTime.toFixed(2);
  canvas.dataset.musicDuration = Number.isFinite(musicState.duration)
    ? musicState.duration.toFixed(2)
    : "unknown";
  canvas.dataset.musicReadyState = String(musicState.readyState);
  canvas.dataset.musicError = musicState.error;
}

function resizeRenderer(): void {
  if (!renderer || !composer) return;
  const width = window.innerWidth;
  const height = window.innerHeight;
  renderer.setSize(width, height, false);
  composer.setSize(width, height);
  if (gameCamera) {
    gameCamera.aspect = width / Math.max(1, height);
    gameCamera.updateProjectionMatrix();
  }
}

async function fetchWorldConfig(): Promise<WorldApiResponse> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 4500);
  try {
    const response = await fetch("/api/world", {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    const contentType = response.headers.get("Content-Type") ?? "";
    if (!response.ok || !contentType.includes("application/json")) throw new Error("觀測站尚未連線");
    const data: unknown = await response.json();
    if (!isWorldApiResponse(data)) throw new Error("世界資料格式不正確");
    return data;
  } catch {
    const today = new Date().toISOString().slice(0, 10);
    return {
      schemaVersion: 1,
      worldVersion: "1.0.0-local",
      buildId: "local-preview",
      dailySeed: hashString(today),
      dayKey: today,
      weather: { preset: "breeze", windStrength: 0.82, cloudDensity: 0.68 },
      spawn: { x: 0, y: 18, z: 28 },
      regions: [
        { id: "windward-meadow", label: "起風草原" },
        { id: "hanging-gardens", label: "垂瀑花園" },
        { id: "copperwing-workshop", label: "銅翼工坊" },
        { id: "stargazer-sanctum", label: "星盤聖所" },
      ],
      message: "本機航線就緒。風正在等你。",
    };
  } finally {
    window.clearTimeout(timer);
  }
}

function loadSave(config: WorldApiResponse): SaveData {
  const fallback: SaveData = {
    version: 1,
    worldVersion: config.worldVersion,
    checkpoint: { ...config.spawn },
    activatedCores: [],
    collectedShards: [],
    quality: detectQuality(),
    soundEnabled: true,
    updatedAt: new Date().toISOString(),
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (!isSaveData(parsed)) {
      localStorage.removeItem(STORAGE_KEY);
      return fallback;
    }
    if (parsed.worldVersion !== config.worldVersion) {
      return {
        ...fallback,
        quality: parsed.quality,
        soundEnabled: parsed.soundEnabled,
      };
    }
    return parsed;
  } catch {
    return fallback;
  }
}

function isWorldApiResponse(value: unknown): value is WorldApiResponse {
  if (!isRecord(value)) return false;
  return value.schemaVersion === 1
    && typeof value.worldVersion === "string"
    && typeof value.buildId === "string"
    && typeof value.dailySeed === "number"
    && isRecord(value.weather)
    && typeof value.weather.cloudDensity === "number"
    && isRecord(value.spawn)
    && typeof value.spawn.x === "number"
    && typeof value.spawn.y === "number"
    && typeof value.spawn.z === "number"
    && typeof value.message === "string";
}

function isSaveData(value: unknown): value is SaveData {
  if (!isRecord(value) || value.version !== 1 || !isRecord(value.checkpoint)) return false;
  return typeof value.worldVersion === "string"
    && typeof value.checkpoint.x === "number"
    && typeof value.checkpoint.y === "number"
    && typeof value.checkpoint.z === "number"
    && Array.isArray(value.activatedCores)
    && value.activatedCores.every((item) => typeof item === "string")
    && Array.isArray(value.collectedShards)
    && value.collectedShards.every((item) => typeof item === "string")
    && (value.quality === "low" || value.quality === "medium" || value.quality === "high")
    && typeof value.soundEnabled === "boolean"
    && typeof value.updatedAt === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function supportsWebGL(): boolean {
  try {
    const testCanvas = document.createElement("canvas");
    return Boolean(
      testCanvas.getContext("webgl2", { failIfMajorPerformanceCaveat: false })
      || testCanvas.getContext("webgl", { failIfMajorPerformanceCaveat: false }),
    );
  } catch {
    return false;
  }
}

function detectQuality(): QualityLevel {
  const navigatorWithMemory = navigator as Navigator & { deviceMemory?: number };
  const coarse = matchMedia("(pointer: coarse)").matches;
  const memory = navigatorWithMemory.deviceMemory ?? 8;
  if (coarse || memory <= 4 || window.innerWidth < 760) return "low";
  if (memory >= 12 && window.devicePixelRatio <= 2) return "high";
  return "medium";
}

function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}
